import React, { createContext, useContext, useState, useCallback } from 'react';
import { Loading } from '../components/ui';
import { LoadingVariant, LoadingAnimation } from '../components/ui/Loading';

interface LoadingOptions {
  message?: string;
  variant?: LoadingVariant;
  animation?: LoadingAnimation;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

interface LoadingContextType {
  showLoading: (options?: LoadingOptions) => void;
  hideLoading: () => void;
  isLoading: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<LoadingOptions>({});

  const showLoading = useCallback((opts: LoadingOptions = {}) => {
    setOptions(opts);
    setIsLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <LoadingContext.Provider value={{ showLoading, hideLoading, isLoading }}>
      {children}
      {isLoading && (
        <Loading
          variant={options.variant || 'overlay'}
          animation={options.animation || 'breathing'}
          message={options.message}
          size={options.size || 'lg'}
        />
      )}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};
